/**
 * Feature flags hook — loads store features and active plugins from the backend.
 * Plugin must be active (WordPress-style); settings toggles apply only when the plugin is on.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { isPluginActive } from '@shared/plugins/registry'

export interface FeatureFlags {
  enable_reviews: boolean
  enable_wishlist: boolean
  enable_guest_checkout: boolean
  enable_share_products: boolean
  enable_qr_code: boolean
  maintenance_mode: boolean
  enable_coupons: boolean
  enable_promotions: boolean
  enable_contact_form: boolean
  enable_whatsapp_button: boolean
  enable_returns: boolean
  enable_loyalty: boolean
  enable_static_pages: boolean
  enable_home_banner_slider: boolean
  enable_blog: boolean
  enable_savings_schemes: boolean
  enable_shipping: boolean
  enable_social_login: boolean
  oauth_google_enabled: boolean
  oauth_facebook_enabled: boolean
  active_plugins: string[]
}

const DEFAULT_FEATURES: FeatureFlags = {
  enable_reviews: false,
  enable_wishlist: false,
  enable_guest_checkout: true,
  enable_share_products: true,
  enable_qr_code: true,
  maintenance_mode: false,
  enable_coupons: false,
  enable_promotions: false,
  enable_contact_form: false,
  enable_whatsapp_button: false,
  enable_returns: false,
  enable_loyalty: false,
  enable_static_pages: false,
  enable_home_banner_slider: false,
  enable_blog: false,
  enable_savings_schemes: false,
  enable_shipping: false,
  enable_social_login: false,
  oauth_google_enabled: false,
  oauth_facebook_enabled: false,
  active_plugins: [],
}

export function useFeatures() {
  const { data: features, isLoading, refetch } = useQuery({
    queryKey: ['storefront', 'features'],
    queryFn: async () => {
      const { data } = await api.get<{ data: FeatureFlags }>('/config/features')
      return data.data
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  return {
    features: features ?? DEFAULT_FEATURES,
    isLoading,
    refetch,
  }
}

/** Plugin is active in the database (installed + activated). */
export function useIsPluginActive(pluginId: string) {
  const { features, isLoading } = useFeatures()
  return {
    isActive: isPluginActive(features.active_plugins, pluginId),
    isLoading,
  }
}
